import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';
import { lightTheme } from './config/theme';
import LanguageSelector from './components/LanguageSelector';

// Route-level code splitting: each page becomes its own JS chunk so the
// landing/register page doesn't have to download the entire admin panel.
const RegistrationForm = lazy(() => import('./components/RegistrationForm/RegistrationForm'));
const EliteRegistrationForm = lazy(() => import('./components/EliteRegistration/EliteRegistrationForm'));
const EliteDashboard = lazy(() => import('./components/EliteRegistration/EliteDashboard'));
const EliteLogin = lazy(() => import('./components/EliteRegistration/EliteLogin'));
const EliteUserAccount = lazy(() => import('./components/EliteRegistration/EliteUserAccount'));
const AdminLogin = lazy(() => import('./components/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const BorrowingForm = lazy(() => import('./components/BorrowingForm/BorrowingForm'));
const EducationForm = lazy(() => import('./components/EducationForm/EducationForm'));
const StudentRegistration = lazy(() => import('./components/EducationForm/StudentRegistration'));
const StudentAttendance = lazy(() => import('./components/EducationForm/StudentAttendance'));
const ManagerDashboard = lazy(() => import('./components/Manager/ManagerDashboard'));
const ProjectManagerBoard = lazy(() => import('./components/TrainingKits/ProjectManagerBoard'));
const EmployeeLogin = lazy(() => import('./components/Employee/EmployeeLogin'));
const EmployeeDashboard = lazy(() => import('./components/Employee/EmployeeDashboard'));
const WorkshopRegistration = lazy(() => import('./components/WorkshopRegistration/WorkshopRegistration'));
const WorkshopSurvey = lazy(() => import('./components/WorkshopSurvey/WorkshopSurvey'));
const PublicVolunteerReport = lazy(() => import('./components/Public/PublicVolunteerReport'));
const PublicAttendanceReport = lazy(() => import('./components/Public/PublicAttendanceReport'));
const PublicOvertimeApproval = lazy(() => import('./components/Public/PublicOvertimeApproval'));
const FablabVisitForm = lazy(() => import('./components/FablabVisit/FablabVisitForm'));
const PublicFablabVisitApproval = lazy(() => import('./components/Public/PublicFablabVisitApproval'));
const ProjectSupportForm = lazy(() => import('./components/ProjectSupport/ProjectSupportForm'));
const PublicProjectSupportApproval = lazy(() => import('./components/Public/PublicProjectSupportApproval'));
const StorePage = lazy(() => import('./components/Store/StorePage'));
const MyOrdersPage = lazy(() => import('./components/Store/MyOrdersPage'));
const PrintServicePage = lazy(() => import('./components/Print3D/PrintServicePage'));
const PrintQuotePage = lazy(() => import('./components/Print3D/PrintQuotePage'));
const PaymentPage = lazy(() => import('./components/PaymentPage/PaymentPage'));
// Public website (replaces the old main.fablabsahsa.com)
const SiteHome = lazy(() => import('./components/Site/SiteHome'));
const SiteAbout = lazy(() => import('./components/Site/SiteAbout'));
const SitePaths = lazy(() => import('./components/Site/SitePaths'));
const SiteSections = lazy(() => import('./components/Site/SiteSections'));
const SiteFacilities = lazy(() => import('./components/Site/SiteFacilities'));
const SiteServices = lazy(() => import('./components/Site/SiteServices'));
const SiteMedia = lazy(() => import('./components/Site/SiteMedia'));
const SiteStories = lazy(() => import('./components/Site/SiteStories'));
const SiteWorkshops = lazy(() => import('./components/Site/SiteWorkshops'));
const SiteInternship = lazy(() => import('./components/Site/SiteInternship'));
const SiteTalented = lazy(() => import('./components/Site/SiteTalented'));

// Public website pages carry their own header (language switch, menus),
// so the floating language selector is hidden there.
const SITE_PATHS = new Set(['/', '/about', '/aboutfablab', '/paths', '/sections', '/oursections', '/facilities', '/services', '/media', '/success-stories', '/workshops', '/internship', '/i-am-talented', '/i_am_talented']);
const isSitePath = (pathname) => SITE_PATHS.has((pathname.replace(/\/+$/, '') || '/').toLowerCase());

// "/" is the website home. Old links / saved drafts pointed the
// registration form at "/?step=…" — send any query on "/" to /register.
const RootRoute = () => {
  const { search } = useLocation();
  if (search && search.length > 1) return <Navigate to={`/register${search}`} replace />;
  return <SiteHome />;
};

// Boards remember their own theme (dark by default) — match it so the
// loader doesn't flash white before a dark dashboard.
const boardIsDark = (path) => {
  try {
    if (/^\/(admin\/dashboard|manager\/)/.test(path)) return (localStorage.getItem('adminTheme') || 'dark') === 'dark';
    if (/^\/(employee\/dashboard|projects)/.test(path)) return (localStorage.getItem('employeeTheme') || 'dark') === 'dark';
  } catch { /* storage blocked */ }
  return false;
};

const RouteFallback = () => {
  // Website pages are dark — avoid a white flash while their chunk loads.
  const path = useLocation().pathname;
  const site = isSitePath(path);
  const board = !site && boardIsDark(path);
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: site ? '#0b0f1d' : board ? '#07080f' : 'var(--gray-50, #f9fafb)'
    }}>
      <div style={{
        width: 42, height: 42,
        border: site ? '3px solid rgba(156, 39, 176, 0.2)' : '3px solid rgba(238, 35, 41, 0.15)',
        borderTopColor: site ? '#9C27B0' : '#EE2329',
        borderRadius: '50%',
        animation: 'spin 0.9s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// Wrapper to conditionally show LanguageSelector (hide on admin/manager/elite pages)
const ConditionalLanguageSelector = () => {
  const location = useLocation();
  if (isSitePath(location.pathname)) return null;
  if (location.pathname === '/admin/login' || location.pathname.startsWith('/manager') || location.pathname.startsWith('/elite') || location.pathname.startsWith('/employee') || location.pathname.startsWith('/projects') || location.pathname.startsWith('/workshop') || location.pathname.startsWith('/public/') || location.pathname.startsWith('/fablab-visit') || location.pathname.startsWith('/project-support') || location.pathname.startsWith('/store') || location.pathname.startsWith('/print-service') || location.pathname.startsWith('/print-quote') || location.pathname.startsWith('/pay/')) return null;
  return <LanguageSelector />;
};

const AppContent = () => {
  return (
    <>
      <ConditionalLanguageSelector />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/register" element={<RegistrationForm />} />
          {/* Public website */}
          <Route path="/about" element={<SiteAbout />} />
          <Route path="/AboutFablab" element={<Navigate to="/about" replace />} />
          <Route path="/paths" element={<SitePaths />} />
          <Route path="/sections" element={<SiteSections />} />
          <Route path="/oursections" element={<Navigate to="/sections" replace />} />
          <Route path="/facilities" element={<SiteFacilities />} />
          <Route path="/services" element={<SiteServices />} />
          <Route path="/media" element={<SiteMedia />} />
          <Route path="/success-stories" element={<SiteStories />} />
          <Route path="/workshops" element={<SiteWorkshops />} />
          <Route path="/volunteer" element={<Navigate to="/" replace />} />
          <Route path="/internship" element={<SiteInternship />} />
          <Route path="/i-am-talented" element={<SiteTalented />} />
          <Route path="/i_am_talented" element={<Navigate to="/i-am-talented" replace />} />
          <Route path="/elite-registration" element={<EliteRegistrationForm />} />
          <Route path="/elite-dashboard" element={<EliteDashboard />} />
          <Route path="/elite/login" element={<EliteLogin />} />
          <Route path="/elite/account" element={<EliteUserAccount />} />
          <Route path="/borrow" element={<BorrowingForm />} />
          <Route path="/educate" element={<EducationForm />} />
          <Route path="/educate/students" element={<StudentRegistration />} />
          <Route path="/educate/attendance" element={<StudentAttendance />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
          <Route path="/manager/dashboard/*" element={<ManagerDashboard />} />
          <Route path="/workshop" element={<WorkshopRegistration />} />
          {/* Shareable education-workshop URL. Pre-selects the
              workshop; skips the picker step. */}
          <Route path="/workshop/:workshopId" element={<WorkshopRegistration />} />
          <Route path="/workshop-survey/:token" element={<WorkshopSurvey />} />
          <Route path="/employee/login" element={<EmployeeLogin />} />
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          <Route path="/projects" element={<ProjectManagerBoard />} />
          {/* Public no-login volunteer report pages (token-gated) */}
          <Route path="/public/volunteer/:token" element={<PublicVolunteerReport />} />
          <Route path="/public/report/:token" element={<PublicAttendanceReport />} />
          <Route path="/public/overtime/:token" element={<PublicOvertimeApproval />} />
          <Route path="/fablab-visit" element={<FablabVisitForm />} />
          <Route path="/public/fablab-visit/:token" element={<PublicFablabVisitApproval />} />
          <Route path="/project-support" element={<ProjectSupportForm />} />
          <Route path="/public/project-support/:token" element={<PublicProjectSupportApproval />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/store/my-orders" element={<MyOrdersPage />} />
          <Route path="/print-service" element={<PrintServicePage />} />
          <Route path="/print-quote/:token" element={<PrintQuotePage />} />
          {/* Payment link created in the admin "صفحات الدفع" tab */}
          <Route path="/pay/:slug" element={<PaymentPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </ThemeProvider>
  );
}

export default App;
